import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState
} from 'react'

import { fetchAll } from '@/services/operators/fetchAll'
import { BaseFilters, useCommonContext } from '@/hooks/use-common-context'

interface IGeneralProps {
  activeDrawer: boolean
  currentFilters: BaseFilters | undefined
  handleFilter: (filtros: BaseFilters) => Promise<void>
  handleLimitChange: (newLimit: number) => void
  handleSelectItem: (item: string[], action: boolean) => void
  isLoading: boolean
  limit: number
  operator: any
  page: number
  results: number
  search: string
  selectItems: any[]
  setActiveDrawer: React.Dispatch<React.SetStateAction<boolean>>
  setCurrentFilters: React.Dispatch<React.SetStateAction<BaseFilters | undefined>>
  setLimit: React.Dispatch<React.SetStateAction<number>>
  setOperator: React.Dispatch<React.SetStateAction<any>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setResults: React.Dispatch<React.SetStateAction<number>>
  setSearch: React.Dispatch<React.SetStateAction<string>>
  setSelectItems: React.Dispatch<React.SetStateAction<any[]>>
  setTableData: React.Dispatch<React.SetStateAction<any[]>>
  tableData: any[]
  triggerUnitListRefetch: () => void
  unitListRefetchTrigger: number
}

const GeneralContext = createContext<IGeneralProps | undefined>(undefined)

const GeneralProvider = ({ children }: { children: ReactNode }) => {
  const [operator, setOperator] = useState<any>(null)
  const [unitListRefetchTrigger, setUnitListRefetchTrigger] = useState(0)

  const triggerUnitListRefetch = () => setUnitListRefetchTrigger(prev => prev + 1)

  const {
    activeDrawer, currentFilters, handleFilter, handleLimitChange,
    handleSelectItem, isLoading, limit, page, results, search, selectItems,
    setActiveDrawer, setCurrentFilters, setLimit, setPage, setResults,
    setSearch, setSelectItems, setTableData, tableData,
  } = useCommonContext(fetchAll, {
    grupoEconomicoEmpty: '',
    afterFilter: triggerUnitListRefetch,
  })

  const contextValue = useMemo(
    () => ({
      activeDrawer,
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      operator,
      page,
      results,
      search,
      selectItems,
      setActiveDrawer,
      setCurrentFilters,
      setLimit,
      setOperator,
      setPage,
      setResults,
      setSearch,
      setSelectItems,
      setTableData,
      tableData,
      triggerUnitListRefetch,
      unitListRefetchTrigger,
    }),
    [
      activeDrawer,
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      operator,
      page,
      results,
      search,
      selectItems,
      tableData,
      unitListRefetchTrigger,
    ]
  )

  return (
    <GeneralContext.Provider value={contextValue}>
      {children}
    </GeneralContext.Provider>
  )
}

const useGeneralContext = () => {
  const context = useContext(GeneralContext)
  if (context === undefined) {
    throw new Error('useGeneralContext must be used within a GeneralProvider')
  }
  return context
}

export { GeneralProvider, useGeneralContext }
